/**
 * F45 (Wave 2D) — per-venue / per-sport / per-trainer scorecard.
 *
 * Routes: `/analytics/scorecards/venues/:id`, `/analytics/scorecards/sports/:id`,
 * `/analytics/scorecards/trainers/:id`. Permission: `analytics:read`.
 *
 * KPI strip + funnel snapshot + repeat-customer rate + open-game/tournament
 * counts + PoP + deep-link Buttons into the owning admin surfaces (F42 venue,
 * F43 user, F44 financials via /finance/settlements?entityId=).
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import { Badge, Banner, Button, EmptyState, Skeleton, Stat, Switch } from '@bravemove/ui-web';
import type { AnalyticsKpiCard, AnalyticsScorecard } from '@bravemove/types';
import { adminAnalyticsApi, type ScorecardEntity } from '../../api/analytics';
import { ApiError } from '../../auth/types';
import { PageHeader } from '../../components/PageHeader';
import { AnalyticsNav } from './AnalyticsNav';
import { FunnelPanel } from './components/FunnelPanel';
import { PercentDelta } from './components/PercentDelta';
import {
  defaultAnalyticsRange,
  deltaTone,
  roundPercent,
  type AnalyticsRangeValue,
} from './analyticsHelpers';

export interface AnalyticsScorecardPageProps {
  /** Which scorecard kind — wired per-route in App.tsx (venues / sports / trainers). */
  readonly entity: ScorecardEntity;
}

const TITLE_KEY: Record<ScorecardEntity, string> = {
  venues: 'admin:analytics.scorecard.venueTitle',
  sports: 'admin:analytics.scorecard.sportTitle',
  trainers: 'admin:analytics.scorecard.trainerTitle',
};

/**
 * Labels keyed by the backend's `deepLinks` record keys — the contract with
 * `deepLinksForScorecard` in apps/api/src/services/analytics-read.ts (`venue` /
 * `user` / `financials`). Both ends must agree or the buttons render raw key
 * text (the analytics-scorecard-deeplinks-broken audit fix).
 */
const DEEP_LINK_LABEL: Record<string, string> = {
  venue: 'admin:analytics.scorecard.viewInVenues',
  user: 'admin:analytics.scorecard.viewInUsers',
  financials: 'admin:analytics.scorecard.viewFinancials',
};

export function AnalyticsScorecardPage({ entity }: AnalyticsScorecardPageProps) {
  const { t } = useI18n();
  const { id } = useParams<{ id: string }>();

  const [range] = useState<AnalyticsRangeValue>(() => defaultAnalyticsRange());
  const [compare, setCompare] = useState(false);
  const [scorecard, setScorecard] = useState<AnalyticsScorecard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  const fetchScorecard = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    setNotFound(false);
    try {
      setScorecard(
        await adminAnalyticsApi.getScorecard(entity, id, {
          from: range.from,
          to: range.to,
          compare,
        }),
      );
    } catch (err) {
      const code = err instanceof ApiError ? err.code : (err as { code?: string })?.code;
      if (code === 'ANALYTICS_ENTITY_NOT_FOUND') setNotFound(true);
      else setError(t('admin:analytics.error'));
    } finally {
      setLoading(false);
    }
  }, [entity, id, range.from, range.to, compare, t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchScorecard();
  }, [fetchScorecard]);

  return (
    <div className="space-y-6" data-testid="analytics-scorecard-page" data-entity={entity}>
      <PageHeader
        eyebrow="Analytics"
        title={t(TITLE_KEY[entity])}
        {...(scorecard?.entityName ? { subtitle: scorecard.entityName } : {})}
        actions={
          <Switch
            label={t('admin:analytics.period.compareToPrevious')}
            checked={compare}
            onChange={(e) => setCompare(e.target.checked)}
            data-testid="analytics-scorecard-compare"
          />
        }
      />
      <AnalyticsNav />

      {error ? <Banner tone="danger">{error}</Banner> : null}

      {notFound ? (
        <EmptyState
          title={t('admin:analytics.scorecard.notFound')}
          data-testid="analytics-scorecard-not-found"
        />
      ) : loading || !scorecard ? (
        <Skeleton className="h-72 w-full rounded-xl" data-testid="analytics-scorecard-loading" />
      ) : (
        <>
          {/* Deep-links into the owning admin surfaces (F42 venue / F43 user /
              F44 financials at /finance/settlements?entityId=). */}
          <div
            className="flex flex-wrap items-center gap-2"
            data-testid="analytics-scorecard-deeplinks"
          >
            {Object.entries(scorecard.deepLinks).map(([key, path]) => (
              <Link key={key} to={path} data-testid={`analytics-scorecard-deeplink-${key}`}>
                <Button variant="secondary" size="sm">
                  {DEEP_LINK_LABEL[key] ? t(DEEP_LINK_LABEL[key]!) : key}
                </Button>
              </Link>
            ))}
          </div>

          {/* KPI strip */}
          <div
            className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5"
            data-testid="analytics-scorecard-kpis"
          >
            {scorecard.kpis.map((card) => (
              <KpiCard key={card.key} card={card} compare={compare} />
            ))}
          </div>

          {/* Secondary metrics */}
          <div
            className="flex flex-wrap items-center gap-3"
            data-testid="analytics-scorecard-metrics"
          >
            {scorecard.repeatCustomerRate !== undefined ? (
              <Badge tone="accent">
                {t('admin:analytics.scorecard.repeatCustomerRate')}:{' '}
                {Math.round(scorecard.repeatCustomerRate * 1000) / 10}%
              </Badge>
            ) : null}
            {scorecard.avgRating !== undefined ? (
              <Badge tone="info">
                {t('admin:analytics.scorecard.avgRating')}: {scorecard.avgRating}
              </Badge>
            ) : null}
            <Badge tone="neutral">
              {t('admin:analytics.scorecard.openGameCount')}: {scorecard.openGameCount}
            </Badge>
            <Badge tone="neutral">
              {t('admin:analytics.scorecard.tournamentCount')}: {scorecard.tournamentCount}
            </Badge>
          </div>

          {/* Funnel snapshot */}
          <section className="rounded-xl border border-hairline bg-surface p-4">
            <h2 className="mb-3 font-mono text-[11px] font-bold uppercase tracking-wider text-ink-3">
              {t('admin:analytics.scorecard.funnelSnapshot')}
            </h2>
            <FunnelPanel result={scorecard.funnel} testId="analytics-scorecard-funnel" />
          </section>
        </>
      )}
    </div>
  );
}

function KpiCard({ card, compare }: { card: AnalyticsKpiCard; compare: boolean }) {
  const { t } = useI18n();
  const showDelta = compare && card.deltaPercent !== undefined;
  // Percent KPIs render at 1-decimal precision — the backend emits raw floats.
  const value =
    card.unit === 'percent' ? `${roundPercent(card.value)}%` : card.value.toLocaleString();
  const delta: ReactNode = showDelta ? (
    <PercentDelta deltaPercent={card.deltaPercent!} label={t('admin:analytics.vsPrevious')} />
  ) : null;
  return (
    <div data-testid={`analytics-scorecard-kpi-${card.key}`}>
      <Stat
        label={t(`admin:analytics.kpi.${card.key}`)}
        value={value}
        {...(delta ? { delta } : {})}
        deltaTone={showDelta ? deltaTone(card.deltaPercent!) : 'neutral'}
      />
    </div>
  );
}

export default AnalyticsScorecardPage;
